import { useEffect } from 'react';

import { cart, cartItemFromProduct } from '@/features/marketplace/stores/cart';
import {
  useClaimSuggestedCart,
  useMarketplace,
  useSuggestedCart,
} from '@/lib/api/hooks';

// Deduplicate in-flight claims across layout remounts. Applied suggestions
// live with the persisted, owner-scoped cart so retries preserve removals,
// including after reload, and logout/owner changes reset them with the cart.
const pendingClaims = new Set<string>();

/**
 * Pre-fills the patient's client-side cart from an admin-assigned
 * suggested cart (patient profile builder), once. The lines are added
 * first and only then is the cart claimed server-side, so a failed or
 * abandoned page load can't lose the suggestion.
 */
export function useHydrateSuggestedCart() {
  const { data: suggestedCart } = useSuggestedCart();
  const { data: marketplace } = useMarketplace();
  const { mutateAsync: claim } = useClaimSuggestedCart();

  useEffect(() => {
    if (!suggestedCart || pendingClaims.has(suggestedCart.id)) return;
    if (!marketplace?.products) return;
    const cartId = suggestedCart.id;
    if (!cart.hasAppliedSuggestion(cartId)) {
      const byId = new Map(marketplace.products.map((p) => [p.id, p]));
      for (const line of suggestedCart.lines) {
        const product = byId.get(line.product_id);
        if (!product) continue;
        cart.add(cartItemFromProduct(product), line.quantity);
      }
      cart.markSuggestionApplied(cartId);
    }
    pendingClaims.add(cartId);
    // Promise cleanup still runs if the layout unmounts during the request.
    // A failed claim stays pending server-side and is retried on next entry
    // without adding products the patient has already removed.
    void claim(cartId).catch(() => {}).finally(() => pendingClaims.delete(cartId));
  }, [suggestedCart, marketplace, claim]);
}
